import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/dist/client/router";
import Image from "next/image";
import React from "react";
import { ActivitiesQuery_activities, ENUM_ACTIVITY_PRICETYPE } from "../../graphql/types";
import { makeSlugUrl } from "../../helpers/url";
import { Locale, localePath } from "../../routes";
import Box from "../Box";
import Button from "../Button";
import CustomLink from "../CustomLink";
import Flex from "../Flex";
import ItemContainer from "../ItemContainer";

interface Props {
  activities?: (ActivitiesQuery_activities | null)[] | null;
}

const Activities = ({ activities }: Props) => {
  const { t } = useTranslation(['common', 'activities']);
  const { locale } = useRouter();

  const orderedActivities = [...(activities || [])]?.sort(
    (a1, a2) => (a1?.order || 100) - (a2?.order || 100)
  );

  return (
    <Flex flexWrap="wrap" m={-3} mb={5}>
      {orderedActivities?.map((activity) => {
        const selectedValue = activity?.values?.[0];
        return (
          <Flex p={3} width={{ _: 1, md: 1 / 2, lg: 1 / 3 }} key={activity?.id}>
            <ItemContainer css={{ padding: 0 }} flex={1} noBorder={true}>
              <CustomLink
                ariaLabel={activity?.title}
                css={customLinkStyle}
                href={`${localePath[locale as Locale].activities}/${makeSlugUrl(
                  activity
                )}`}
              >
                <Flex p={3} flexDirection="column" flex={1}>
                  <Flex flex={1} flexDirection="column" mb={4}>
                    <Title>{activity?.title}</Title>
                    <Description mt={2}>
                      {activity?.shortDescription}
                    </Description>
                  </Flex>
                  <Flex mb={3} alignItems="center">
                    <Price>
                      {selectedValue?.price?.amount} {t("currency")}
                    </Price>{" "}
                    <span css={{ marginLeft: 6, fontSize: "14px" }}>
                      {activity?.priceType === ENUM_ACTIVITY_PRICETYPE.perActivity
                        ? t("perActivity") : t("perPerson")}
                    </span>
                  </Flex>
                  <Image
                    src={activity?.images?.[0]?.url || "/"}
                    alt={activity?.images?.[0]?.alternativeText || ""}
                    width={300}
                    height={200}
                    layout="responsive"
                  />
                  <Button css={{ marginTop: 16 }}>
                    {t("activities:buyActivity")}
                  </Button>
                </Flex>
              </CustomLink>
            </ItemContainer>
          </Flex>
        );
      })}
    </Flex>
  );
};

export default Activities;

const Title = styled.span`
  font-weight: 700;
  text-transform: uppercase;
  font-family: 'Kodchasan', sans-serif;
  font-size: ${({ theme }) => theme.fontSizes.large};
`;

const Description = styled(Box)`
  font-size: ${({ theme }) => theme.fontSizes.small};
  height: 100%;
`;

const customLinkStyle = css`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const Price = styled.span`
  font-weight: 700;
  text-transform: uppercase;
`;
